import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Container,
    Paper,
    Group,
    Stack,
    Title,
    Text,
    Tabs,
    Button,
    Table,
    Badge,
    Select,
    Loader,
    Center,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import HeroSectionV2 from '../../../../components/HeroSectionV2';
import Billede1 from '../../../../Images/Billlede-1.png';

const apiUrl = process.env.REACT_APP_API_URL;

type TransportAdminEventDto = { eventId: number; name: string };

type AdminTransportBookingDto = {
    transportBookingId: number;
    eventId: number;
    personId: number;
    personName: string;
    email?: string | null;
    whatsappCountryCode?: string | null;
    whatsappNumber?: string | null;
    pickupLocationName: string;
    dropoffLocationName: string;
    pickupDate: string;
    pickupTime: string;
    passengerCount: number;
    status: string;
    createdAtUtc: string;
};

type AdminTransportBookingTotalsDto = {
    pickupDate: string;
    totalBookings: number;
    totalPassengers: number;
};

type AdminTransportBookingSlotDto = {
    pickupDate: string;
    pickupTime: string;
    totalBookings: number;
    totalPassengers: number;
};

export default function TransportAdminPage() {
    const [events, setEvents] = useState<TransportAdminEventDto[]>([]);
    const [eventId, setEventId] = useState<number | null>(null);

    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingBookings, setLoadingBookings] = useState(false);
    const [error, setError] = useState("");

    const [bookingDate, setBookingDate] = useState<string | null>(null);
    const [bookings, setBookings] = useState<AdminTransportBookingDto[]>([]);
    const [bookingTotals, setBookingTotals] = useState<AdminTransportBookingTotalsDto[]>([]);
    const [bookingSlots, setBookingSlots] = useState<AdminTransportBookingSlotDto[]>([]);
    const [activeTab, setActiveTab] = useState<string | null>("bookings");

    const eventOptions = useMemo(
        () => events.map((e) => ({ value: String(e.eventId), label: e.name })),
        [events]
    );

    const bookingDateOptions = useMemo(
        () =>
            bookingTotals.map((t) => ({
                value: t.pickupDate,
                label: `${t.pickupDate} (${t.totalPassengers} pers.)`,
            })),
        [bookingTotals]
    );

    const confirmedPassengerCount = useMemo(
        () =>
            bookings.reduce(
                (sum, b) => sum + (b.status === "confirmed" ? b.passengerCount : 0),
                0
            ),
        [bookings]
    );

    const loadEvents = useCallback(async () => {
        if (!apiUrl) {
            setError("API URL mangler i miljøvariabler.");
            return;
        }

        try {
            setLoadingEvents(true);
            setError("");

            console.time("transport admin events");
            const res = await axios.get(`${apiUrl}/api/transport/admin/events`);
            console.timeEnd("transport admin events");

            setEvents(res.data ?? []);

            if (res.data?.length) {
                setEventId((prev) => prev ?? res.data[0].eventId);
            }
        } catch (err) {
            console.error(err);
            setError("Kunne ikke hente events.");
        } finally {
            setLoadingEvents(false);
        }
    }, []);

    const loadBookingTotals = useCallback(async (eid: number) => {
        if (!apiUrl) return;

        const res = await axios.get(`${apiUrl}/api/transport/admin/bookings/totals`, {
            params: { eventId: eid },
        });

        setBookingTotals(res.data ?? []);
    }, []);

    const loadBookings = useCallback(async (eid: number, date: string | null) => {
        if (!apiUrl) return;

        const res = await axios.get(`${apiUrl}/api/transport/admin/bookings`, {
            params: { eventId: eid, date: date ?? undefined },
        });

        setBookings(res.data ?? []);
    }, []);

    const loadBookingSlots = useCallback(async (eid: number, date: string | null) => {
        if (!apiUrl) return;

        if (!date) {
            setBookingSlots([]);
            return;
        }

        const res = await axios.get(`${apiUrl}/api/transport/admin/bookings/slots`, {
            params: { eventId: eid, date },
        });

        setBookingSlots(res.data ?? []);
    }, []);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    useEffect(() => {
        if (!eventId) return;

        const run = async () => {
            try {
                setLoadingBookings(true);
                setError("");

                console.time("transport admin totals");
                await loadBookingTotals(eventId);
                console.timeEnd("transport admin totals");

                console.time("transport admin bookings");
                await loadBookings(eventId, bookingDate);
                console.timeEnd("transport admin bookings");

                if (bookingDate) {
                    console.time("transport admin slots");
                    await loadBookingSlots(eventId, bookingDate);
                    console.timeEnd("transport admin slots");
                } else {
                    setBookingSlots([]);
                }
            } catch (err) {
                console.error(err);
                setError("Kunne ikke hente bookingdata.");
            } finally {
                setLoadingBookings(false);
            }
        };

        run();
    }, [eventId, bookingDate, loadBookingTotals, loadBookings, loadBookingSlots]);

    const cancelBooking = async (id: number) => {
        if (!eventId || !apiUrl) return;

        try {
            await axios.post(`${apiUrl}/api/transport/admin/bookings/${id}/cancel`);
            showNotification({
                title: "OK",
                message: "Booking annulleret",
            });

            await loadBookings(eventId, bookingDate);
            if (bookingDate) {
                await loadBookingSlots(eventId, bookingDate);
            }
            await loadBookingTotals(eventId);
        } catch (err) {
            console.error(err);
            showNotification({
                title: "Fejl",
                message: "Kunne ikke annullere booking",
                color: "red",
            });
        }
    };

    const onTabChange = async (tab: string | null) => {
        setActiveTab(tab);

        if (!eventId) return;

        try {
            setLoadingBookings(true);
            await loadBookingTotals(eventId);
            await loadBookings(eventId, bookingDate);
            if (bookingDate) {
                await loadBookingSlots(eventId, bookingDate);
            }
        } catch (err) {
            console.error(err);
            setError("Kunne ikke opdatere data.");
        } finally {
            setLoadingBookings(false);
        }
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Transport"
                titleDa="Transport"
                descriptionEn="Setup for day transport"
                descriptionDa="Opsætnings dagstransport"
                imageSrc={Billede1}
            />

            <Container size="xl" py="xl">
                <Paper withBorder p="lg" radius="md">
                    <Group justify="space-between" align="flex-end">
                        <Stack gap={2}>
                            <Title order={3}>Transport Admin</Title>
                            <Text c="dimmed" size="sm">
                                Opret steder, dage/tider og tokens til transport booking
                            </Text>
                        </Stack>

                        <Select
                            label="Vælg event"
                            data={eventOptions}
                            value={eventId ? String(eventId) : null}
                            onChange={(v) => {
                                setBookingDate(null);
                                setEventId(v ? Number(v) : null);
                            }}
                            searchable
                            w={320}
                            disabled={loadingEvents}
                        />
                    </Group>
                </Paper>

                {!!error && (
                    <Paper withBorder p="md" radius="md" mt="md">
                        <Text c="red">{error}</Text>
                    </Paper>
                )}

                <Paper withBorder p="lg" radius="md" mt="md">
                    <Tabs value={activeTab} onChange={onTabChange}>
                        <Tabs.List>
                            <Tabs.Tab value="bookings">Bookinger</Tabs.Tab>
                        </Tabs.List>

                        <Tabs.Panel value="bookings" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Bookinger</Text>
                                <Badge variant="light">
                                    {confirmedPassengerCount} pers.
                                </Badge>
                            </Group>

                            <Paper withBorder p="md" radius="md" mb="md">
                                <Group align="flex-end" justify="space-between" wrap="wrap">
                                    <Select
                                        label="Dato"
                                        placeholder="Alle datoer"
                                        data={bookingDateOptions}
                                        value={bookingDate}
                                        onChange={setBookingDate}
                                        searchable
                                        w={320}
                                        disabled={!eventId || loadingBookings}
                                    />
                                    <Button
                                        variant="default"
                                        onClick={() => setBookingDate(null)}
                                        disabled={loadingBookings}
                                    >
                                        Vis alle
                                    </Button>
                                </Group>
                            </Paper>

                            {loadingEvents || loadingBookings ? (
                                <Center py="xl">
                                    <Loader />
                                </Center>
                            ) : (
                                <>
                                    {bookingDate && bookingSlots.length > 0 && (
                                        <Paper withBorder p="md" radius="md" mb="md">
                                            <Text fw={700} mb="sm">
                                                Timeslots ({bookingDate})
                                            </Text>

                                            <Table.ScrollContainer minWidth={600}>
                                                <Table striped highlightOnHover>
                                                    <Table.Thead>
                                                        <Table.Tr>
                                                            <Table.Th>Tid</Table.Th>
                                                            <Table.Th>Bookinger</Table.Th>
                                                            <Table.Th>Personer</Table.Th>
                                                        </Table.Tr>
                                                    </Table.Thead>
                                                    <Table.Tbody>
                                                        {bookingSlots.map((s) => (
                                                            <Table.Tr key={s.pickupTime}>
                                                                <Table.Td>{s.pickupTime}</Table.Td>
                                                                <Table.Td>{s.totalBookings}</Table.Td>
                                                                <Table.Td>{s.totalPassengers}</Table.Td>
                                                            </Table.Tr>
                                                        ))}
                                                    </Table.Tbody>
                                                </Table>
                                            </Table.ScrollContainer>
                                        </Paper>
                                    )}

                                    <Paper withBorder p="md" radius="md">
                                        <Text fw={700} mb="sm">
                                            Alle bookinger
                                        </Text>

                                        <Table.ScrollContainer minWidth={1100}>
                                            <Table striped highlightOnHover>
                                                <Table.Thead>
                                                    <Table.Tr>
                                                        <Table.Th>Person</Table.Th>
                                                        <Table.Th>Email</Table.Th>
                                                        <Table.Th>Whatsapp</Table.Th>
                                                        <Table.Th>Afhentning</Table.Th>
                                                        <Table.Th>Destination</Table.Th>
                                                        <Table.Th>Dato</Table.Th>
                                                        <Table.Th>Tid</Table.Th>
                                                        <Table.Th>Pers.</Table.Th>
                                                        <Table.Th>Status</Table.Th>
                                                        <Table.Th></Table.Th>
                                                    </Table.Tr>
                                                </Table.Thead>
                                                <Table.Tbody>
                                                    {bookings.map((b) => (
                                                        <Table.Tr key={b.transportBookingId}>
                                                            <Table.Td>{b.personName}</Table.Td>
                                                            <Table.Td>{b.email ?? "-"}</Table.Td>
                                                            <Table.Td>
                                                                {b.whatsappCountryCode && b.whatsappNumber
                                                                    ? `${b.whatsappCountryCode}-${b.whatsappNumber}`
                                                                    : "-"}
                                                            </Table.Td>
                                                            <Table.Td>{b.pickupLocationName}</Table.Td>
                                                            <Table.Td>{b.dropoffLocationName}</Table.Td>
                                                            <Table.Td>{b.pickupDate}</Table.Td>
                                                            <Table.Td>{b.pickupTime}</Table.Td>
                                                            <Table.Td>{b.passengerCount}</Table.Td>
                                                            <Table.Td>
                                                                <Badge
                                                                    color={
                                                                        b.status === "confirmed"
                                                                            ? "green"
                                                                            : "gray"
                                                                    }
                                                                    variant="light"
                                                                >
                                                                    {b.status}
                                                                </Badge>
                                                            </Table.Td>
                                                            <Table.Td>
                                                                <Group justify="flex-end" gap="xs">
                                                                    {b.status === "confirmed" && (
                                                                        <Button
                                                                            size="xs"
                                                                            color="red"
                                                                            variant="light"
                                                                            onClick={() =>
                                                                                cancelBooking(
                                                                                    b.transportBookingId
                                                                                )
                                                                            }
                                                                        >
                                                                            Annullér
                                                                        </Button>
                                                                    )}
                                                                </Group>
                                                            </Table.Td>
                                                        </Table.Tr>
                                                    ))}
                                                </Table.Tbody>
                                            </Table>
                                        </Table.ScrollContainer>
                                    </Paper>
                                </>
                            )}
                        </Tabs.Panel>
                    </Tabs>
                </Paper>
            </Container>
        </div>
    );
}